import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../api/client.ts";
import type { Brand } from "../api/types.ts";
import { useAuth } from "../auth/AuthProvider.tsx";
import { confirmAction, toast } from "../components/feedback.tsx";
import { TimezoneSelect } from "../components/timezones.tsx";
import { BrandBadge, Card, Drawer, Empty, TableSkeleton, useDebounced } from "../components/ui.tsx";
import { useUnsavedChanges } from "../components/unsaved.ts";

interface ContactRow {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  created_at: string;
  suppressed: boolean;
  pulse_stage: string | null;
  pulse_status: string | null;
  pulse_rep: string | null;
  pulse_disposition: string | null;
  /** For the brand picked in the filters; null when none is picked. */
  consent: string | null;
}

interface Facet {
  value: string;
  n: number;
}

/**
 * The filter bar's choices, each kept in the URL so a filtered view survives
 * a refresh, can be shared, and Back undoes the last change.
 */
const FILTER_KEYS = [
  "brand",
  "stage",
  "status",
  "disposition",
  "rep",
  "consent",
  "form",
  "archived",
  "suppressed",
] as const;
type FilterKey = (typeof FILTER_KEYS)[number];
interface PulseFacets {
  stages: Facet[];
  statuses: Facet[];
  reps: Facet[];
  dispositions?: (Facet & { label: string })[];
}
type Condition = { field: string; op: string; value?: string };

/** The bar's choices as a segment filter: the same one "Save as segment" stores. */
/** Bar choices that map straight onto one segment field. */
const SIMPLE_FILTERS: [FilterKey, string][] = [
  ["status", "pulse_close_reason"],
  ["disposition", "pulse_disposition"],
  ["rep", "pulse_rep"],
  ["form", "pulse_from_form"],
  ["archived", "pulse_archived"],
  ["suppressed", "suppressed"],
];

function conditionsFor(f: Record<FilterKey, string>): Condition[] {
  const out: Condition[] = [];
  if (f.stage === "none") out.push({ field: "pulse_stage", op: "is_not_set" });
  else if (f.stage) out.push({ field: "pulse_stage", op: "is", value: f.stage });
  for (const [key, field] of SIMPLE_FILTERS) {
    if (f[key]) out.push({ field, op: "is", value: f[key] });
  }
  if (!f.brand || !f.consent) return out;
  if (f.consent === "cold") {
    // No consent row at all is cold too, which "is cold" alone would miss.
    out.push({ field: "consent", op: "is_not", value: "opted_in" });
    out.push({ field: "consent", op: "is_not", value: "unsubscribed" });
  } else {
    out.push({ field: "consent", op: "is", value: f.consent });
  }
  return out;
}

interface ContactDetail {
  contact: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    company: string | null;
    title: string | null;
    phone: string | null;
    timezone: string | null;
    custom: Record<string, unknown>;
  };
  /** The zone local-time sends use, and whether it was set or inferred from the phone. */
  timezone: { zone: string | null; source: "set" | "phone" | null };
  consent: {
    brand_name: string;
    consent: string;
    consent_source: string | null;
    last_sent_at: string | null;
    last_opened_at: string | null;
    last_replied_at: string | null;
  }[];
  events: {
    type: string;
    occurred_at: string;
    url: string | null;
    is_likely_automated: boolean;
    campaign_name: string | null;
  }[];
  suppressions: { reason: string; detail: string | null; brand_id: string | null }[];
  /** Ranked as segments rank them: the first is the one lists use. */
  pulse: PulseLeadInfo[];
}

interface PulseLeadInfo {
  pulse_lead_id: number;
  process_stage: string | null;
  disposition: string | null;
  lead_status: string | null;
  assigned_to: string | null;
  lead_type: string | null;
  from_form: boolean;
  /** Archived in Pulse; kept for its stage and disposition, never preferred. */
  archived: boolean;
  pulse_created_at: string | null;
  pulse_updated_at: string | null;
  synced_at: string;
  connection_name: string;
  url: string | null;
}

/** Where a consent value came from, in words. */
const CONSENT_SOURCE_LABEL: Record<string, string> = {
  pulse_form: "Pulse: filled in a form",
  pulse: "Pulse lead",
  manual: "set by hand",
  csv: "CSV import",
  import: "CSV import",
};
const consentSourceLabel = (source: string) =>
  CONSENT_SOURCE_LABEL[source] ?? source.replaceAll("_", " ");

/** application_received → Application received */
const stageLabel = (stage: string | null) =>
  stage ? stage.charAt(0).toUpperCase() + stage.slice(1).replaceAll("_", " ") : "—";

interface ListRow {
  id: string;
  name: string;
  brand_name: string;
}

const PAGE = 50;

export function ContactsPage() {
  const { params, offset, filters, setFilter, setOffset, selected, adding, open, close } =
    useContactsUrl();
  // Typed into straight away, asked of the API once typing pauses.
  const [query, setQuery] = useState(params.get("q") ?? "");
  const settledQuery = useDebounced(query.trim(), 300);
  // biome-ignore lint/correctness/useExhaustiveDependencies: only when the settled text changes.
  useEffect(() => {
    if (settledQuery !== (params.get("q") ?? "")) setFilter({ q: settledQuery });
  }, [settledQuery]);
  const conditions = conditionsFor(filters);
  const filtering = conditions.length > 0;
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const q = params.get("q") ?? "";
  const contacts = useQuery({
    queryKey: ["contacts", q, offset, filters],
    queryFn: () => {
      const search = new URLSearchParams({ q, limit: String(PAGE), offset: String(offset) });
      if (filters.brand) search.set("brandId", filters.brand);
      if (filtering) search.set("filter", JSON.stringify({ match: "all", conditions }));
      return api.get<{ rows: ContactRow[]; total: number }>(`/audience/contacts?${search}`);
    },
    // Paging and filtering keep the old rows up until the new ones land,
    // rather than flashing back to placeholders.
    placeholderData: (previous) => previous,
  });
  const facets = useQuery({
    queryKey: ["pulse-facets"],
    queryFn: () => api.get<PulseFacets>("/audience/pulse-facets"),
    staleTime: 5 * 60_000,
  });
  const brands = useQuery({
    queryKey: ["brands"],
    queryFn: () => api.get<Brand[]>("/config/brands"),
  });

  return (
    <div className={`page${picked.size > 0 ? " has-bulk-bar" : ""}`}>
      <div className="page-header">
        <h1>Contacts</h1>
        <p>One record per person, shared across every brand. Consent is tracked per brand.</p>
      </div>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search email, name or company…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {contacts.data && (
          <span style={{ color: "var(--gray-500)" }}>
            {contacts.data.total.toLocaleString()}{" "}
            {contacts.data.total === 1 ? "contact" : "contacts"}
          </span>
        )}
        <div className="spacer" />
        <a
          className="btn ghost"
          href={`/api/audience/contacts/export?${exportQuery(q, filters, conditions)}`}
          download
        >
          Export CSV
        </a>
        {filtering && (
          <button type="button" className="btn ghost" onClick={() => setSaving(true)}>
            Save as segment
          </button>
        )}
        <button type="button" className="btn" onClick={() => open({ new: true })}>
          + Add contact
        </button>
      </div>

      <ContactFilters
        filters={filters}
        filtering={filtering}
        facets={facets.data}
        brands={brands.data ?? []}
        setFilter={setFilter}
      />

      <ContactsTable
        loading={contacts.isLoading}
        rows={contacts.data?.rows}
        searching={Boolean(q) || filtering}
        showConsent={Boolean(filters.brand)}
        selected={selected}
        picked={picked}
        setPicked={setPicked}
        onOpen={(id) => {
          setNotice(null);
          open({ contact: id });
        }}
      />

      {contacts.data && <Pager offset={offset} total={contacts.data.total} onChange={setOffset} />}

      {saving && (
        <SaveSegment
          conditions={conditions}
          brandId={filters.brand}
          brands={brands.data ?? []}
          matches={contacts.data?.total ?? null}
          onClose={() => setSaving(false)}
        />
      )}

      {picked.size > 0 && (
        <AddToList
          contactIds={[...picked]}
          brand={brands.data?.find((b) => b.id === filters.brand) ?? null}
          onDone={() => setPicked(new Set())}
        />
      )}

      {adding && (
        <Drawer
          title="Add a contact"
          subtitle="Adding someone does not opt them in. Set consent per brand once they exist."
          onClose={close}
        >
          <AddContact
            onDone={(id, warning) => {
              setNotice(warning);
              // Replaces the "add" entry, so Back goes to the list rather
              // than to an empty form for someone who now exists.
              open({ contact: id }, { replace: true });
            }}
          />
        </Drawer>
      )}

      {selected && <ContactPanel id={selected} notice={notice} onClose={close} onDeleted={close} />}
    </div>
  );
}

interface ListOption {
  id: string;
  name: string;
  members: number;
  brand_name: string;
}

/**
 * Adding several people to a list at once. One at a time, from each contact's
 * own panel, was the only way — unusable for the case that matters, which is
 * picking a handful of people out of a search.
 */
function AddToList({
  contactIds,
  brand,
  onDone,
}: {
  contactIds: string[];
  /** The brand picked in the filters: what "Unsubscribe" applies to. */
  brand: Brand | null;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const n = contactIds.length;
  const people = `${n.toLocaleString()} ${n === 1 ? "contact" : "contacts"}`;
  const bulk = useMutation({
    mutationFn: (action: "suppress" | "unsubscribe") =>
      api.post<{ updated: number }>("/audience/contacts/bulk", {
        contactIds,
        action,
        ...(brand ? { brandId: brand.id } : {}),
      }),
    onSuccess: (res, action) => {
      toast(
        action === "suppress"
          ? `Suppressed ${res.updated.toLocaleString()} everywhere`
          : `Unsubscribed ${res.updated.toLocaleString()} from ${brand?.name}`,
      );
      void qc.invalidateQueries({ queryKey: ["contacts"] });
      onDone();
    },
    onError: (err: Error) => toast(err.message, "bad"),
  });
  const suppressAll = async () => {
    if (
      await confirmAction({
        title: `Suppress ${people} everywhere?`,
        message: "No brand will mail them, and their scheduled sequence steps stop.",
        confirmLabel: `Suppress ${people}`,
        destructive: true,
      })
    )
      bulk.mutate("suppress");
  };
  const unsubscribeAll = async () => {
    if (!brand) return;
    if (
      await confirmAction({
        title: `Unsubscribe ${people} from ${brand.name}?`,
        message: `${brand.name} will stop mailing them; other brands are unaffected.`,
        confirmLabel: "Unsubscribe",
        destructive: true,
      })
    )
      bulk.mutate("unsubscribe");
  };
  const [listId, setListId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [added, setAdded] = useState<number | null>(null);

  const lists = useQuery({
    queryKey: ["lists"],
    queryFn: () => api.get<ListOption[]>("/audience/lists"),
  });

  const add = useMutation({
    mutationFn: () =>
      api.post<{ added: number }>(`/audience/lists/${listId}/members`, { contactIds }),
    onSuccess: (result) => {
      setAdded(result.added);
      setError(null);
      void qc.invalidateQueries({ queryKey: ["lists"] });
      void qc.invalidateQueries({ queryKey: ["contact-lists"] });
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <section className="bulk-bar" aria-label="Selected contacts">
      <strong>{contactIds.length.toLocaleString()} selected</strong>
      <select
        aria-label="List"
        value={listId}
        onChange={(e) => {
          setListId(e.target.value);
          setAdded(null);
        }}
      >
        <option value="">Add to a list…</option>
        {lists.data?.map((l) => (
          <option key={l.id} value={l.id}>
            {l.name} — {l.brand_name} ({l.members.toLocaleString()})
          </option>
        ))}
      </select>
      <button
        type="button"
        className="btn small"
        disabled={!listId || add.isPending}
        onClick={() => add.mutate()}
      >
        {add.isPending ? "Adding…" : "Add"}
      </button>
      <button
        type="button"
        className="btn ghost small"
        disabled={!brand || bulk.isPending}
        title={brand ? undefined : "Pick a brand in the filters to unsubscribe from it"}
        onClick={unsubscribeAll}
      >
        Unsubscribe{brand ? ` from ${brand.name}` : "…"}
      </button>
      <button
        type="button"
        className="btn ghost small"
        disabled={bulk.isPending}
        onClick={suppressAll}
      >
        Suppress
      </button>
      <button type="button" className="btn ghost small" onClick={onDone}>
        Clear
      </button>
      {error && <div className="bulk-note bad">{error}</div>}
      {added !== null && (
        <div className="bulk-note ok">
          Added {added.toLocaleString()} to the list.
          {added < contactIds.length &&
            ` ${(contactIds.length - added).toLocaleString()} were already on it.`}
        </div>
      )}
      {added === null && !error && (
        <div className="bulk-note">
          Lists belong to one brand. Adding someone to a list is not consent on its own.
        </div>
      )}
    </section>
  );
}

function AddContact({ onDone }: { onDone: (id: string, warning: string | null) => void }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [company, setCompany] = useState("");
  const [error, setError] = useState<string | null>(null);
  useUnsavedChanges(Boolean(email || firstName || lastName || company));

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string; suppressed: boolean; suppressedReason: string | null }>(
        "/audience/contacts",
        {
          email,
          firstName: firstName || null,
          lastName: lastName || null,
          company: company || null,
        },
      ),
    onSuccess: (row) => {
      toast("Contact added");
      void qc.invalidateQueries({ queryKey: ["contacts"] });
      onDone(
        row.id,
        row.suppressed
          ? `Added, but this address is suppressed (${row.suppressedReason?.replaceAll("_", " ")}), so no brand will mail it. An admin can lift that below.`
          : null,
      );
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <Card>
      <form
        className="card-body"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          setError(null);
          create.mutate();
        }}
      >
        {error && <div className="error">{error}</div>}
        <div className="row">
          <div className="field">
            <label htmlFor="c-email">Email</label>
            <input
              id="c-email"
              type="email"
              // biome-ignore lint/a11y/noAutofocus: the drawer opened for this one field.
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="c-company">Company</label>
            <input id="c-company" value={company} onChange={(e) => setCompany(e.target.value)} />
          </div>
        </div>
        <div className="row">
          <div className="field">
            <label htmlFor="c-first">First name</label>
            <input id="c-first" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="c-last">Last name</label>
            <input id="c-last" value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </div>
        </div>
        <button className="btn" type="submit" disabled={create.isPending}>
          {create.isPending ? "Adding…" : "Add contact"}
        </button>
      </form>
    </Card>
  );
}

function ContactPanel({
  id,
  notice,
  onClose,
  onDeleted,
}: {
  id: string;
  notice: string | null;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const qc = useQueryClient();
  const detail = useQuery({
    queryKey: ["contact", id],
    queryFn: () => api.get<ContactDetail>(`/audience/contacts/${id}`),
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["contact", id] });
    void qc.invalidateQueries({ queryKey: ["contacts"] });
  };

  if (!detail.data) {
    return (
      <Drawer title="Loading…" onClose={onClose}>
        {detail.isError ? (
          <div className="error">Could not load this contact.</div>
        ) : (
          <p style={{ color: "var(--gray-500)" }}>Loading contact…</p>
        )}
      </Drawer>
    );
  }
  const { contact, events, suppressions } = detail.data;
  const suppressed = suppressions.some((s) => s.brand_id === null);
  const name = [contact.firstName, contact.lastName].filter(Boolean).join(" ");

  return (
    <Drawer
      title={name || contact.email}
      subtitle={
        <>
          <span className="mono">{contact.email}</span>
          {contact.company && ` · ${contact.company}`}
          {suppressed && (
            <span className="pill bad" style={{ marginLeft: 8 }}>
              Suppressed
            </span>
          )}
        </>
      }
      onClose={onClose}
    >
      {notice && <div className="notice warn">{notice}</div>}
      <PulseCard leads={detail.data.pulse} />
      <Card title="Details" subtitle="The address is the contact's identity and cannot be edited.">
        <Details contact={contact} zone={detail.data.timezone} onSaved={refresh} />
        <Suppression
          contactId={id}
          suppressed={suppressed}
          reasons={suppressions}
          onChanged={refresh}
        />
      </Card>

      <ConsentCard contactId={id} consent={detail.data.consent} onChanged={refresh} />
      <ListsCard contactId={id} />

      <Card title="Activity" subtitle="Most recent 50 events">
        <table>
          <tbody>
            {events.length === 0 ? (
              <tr>
                <td style={{ color: "var(--gray-500)" }}>Nothing recorded.</td>
              </tr>
            ) : (
              events.map((e, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: a read-only log that never reorders; two events can share a type and timestamp.
                <tr key={`${e.type}-${e.occurred_at}-${i}`}>
                  <td className="mono" style={{ width: 170 }}>
                    {new Date(e.occurred_at).toLocaleString()}
                  </td>
                  <td>
                    <span className="pill mute">{e.type}</span>
                    {e.is_likely_automated && (
                      <span className="pill warn" style={{ marginLeft: 6 }}>
                        automated
                      </span>
                    )}
                  </td>
                  <td>{e.campaign_name ?? "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      <DeleteContact contactId={id} email={contact.email} onDeleted={onDeleted} />
    </Drawer>
  );
}

function Details({
  contact,
  zone,
  onSaved,
}: {
  contact: ContactDetail["contact"];
  zone: ContactDetail["timezone"];
  onSaved: () => void;
}) {
  const [form, setForm] = useState(() => detailsForm(contact));
  const [saved, setSaved] = useState(false);
  useUnsavedChanges(JSON.stringify(form) !== JSON.stringify(detailsForm(contact)));

  useEffect(() => {
    setForm(detailsForm(contact));
  }, [contact]);

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/audience/contacts/${contact.id}`, {
        firstName: form.firstName || null,
        lastName: form.lastName || null,
        company: form.company || null,
        title: form.title || null,
        phone: form.phone || null,
        timezone: form.timezone || null,
      }),
    onSuccess: () => {
      toast("Details saved");
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onSaved();
    },
  });

  const field = (key: keyof typeof form, label: string) => (
    <div className="field">
      <label htmlFor={`d-${key}`}>{label}</label>
      <input
        id={`d-${key}`}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
      />
    </div>
  );

  const custom = Object.entries(contact.custom ?? {});

  return (
    <form
      className="card-body"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="row">
        {field("firstName", "First name")}
        {field("lastName", "Last name")}
      </div>
      <div className="row">
        {field("company", "Company")}
        {field("title", "Title")}
      </div>
      <div className="row">
        {field("phone", "Phone")}
        <TimezoneField
          id={`tz-${contact.id}`}
          value={form.timezone}
          zone={zone}
          onChange={(timezone) => setForm({ ...form, timezone })}
        />
      </div>

      {custom.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div className="label-mono">Custom fields from imports</div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 4 }}>
            {custom.map(([key, value]) => (
              <div key={key}>
                <span style={{ color: "var(--gray-500)" }}>{key}: </span>
                <span className="mono">{String(value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <button className="btn small" type="submit" disabled={save.isPending}>
        {saved ? "Saved" : "Save details"}
      </button>
    </form>
  );
}

function Suppression({
  contactId,
  suppressed,
  reasons,
  onChanged,
}: {
  contactId: string;
  suppressed: boolean;
  reasons: ContactDetail["suppressions"];
  onChanged: () => void;
}) {
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const suppress = useMutation({
    mutationFn: () => api.post(`/audience/contacts/${contactId}/suppress`),
    onSuccess: () => {
      onChanged();
      toast("Suppressed everywhere");
    },
    onError: (err: Error) => setError(err.message),
  });
  const lift = useMutation({
    mutationFn: () => api.del(`/audience/contacts/${contactId}/suppress`),
    onSuccess: () => {
      onChanged();
      toast("Suppression lifted");
    },
    onError: (err: Error) => setError(err.message),
  });

  const global = reasons.filter((r) => r.brand_id === null);

  return (
    <div className="card-body subsection-row">
      {error && <div className="error">{error}</div>}
      {suppressed ? (
        <>
          <div>
            <span className="pill bad">Suppressed everywhere</span>{" "}
            <span style={{ color: "var(--gray-500)" }}>
              {global.map((r) => r.reason.replaceAll("_", " ")).join(", ")}
            </span>
          </div>
          {user?.role === "admin" ? (
            <button
              type="button"
              className="btn ghost small"
              onClick={async () => {
                const complaint = global.some((r) => r.reason === "complaint");
                if (
                  await confirmAction({
                    title: "Lift this suppression?",
                    message: complaint
                      ? "This address marked your mail as spam. Mailing it again risks the reputation of every domain that sends to it."
                      : "Every brand will be able to mail this address again.",
                    confirmLabel: "Lift suppression",
                    destructive: complaint,
                  })
                )
                  lift.mutate();
              }}
            >
              Lift suppression
            </button>
          ) : (
            <span className="hint">An admin can lift this.</span>
          )}
        </>
      ) : (
        <>
          <span style={{ color: "var(--gray-500)" }}>
            Not suppressed. Suppressing stops every brand mailing this address.
          </span>
          <button
            type="button"
            className="btn ghost small"
            onClick={async () => {
              if (
                await confirmAction({
                  title: "Suppress this address everywhere?",
                  message: "No brand will mail it, and scheduled sequence steps stop too.",
                  confirmLabel: "Suppress everywhere",
                  destructive: true,
                })
              )
                suppress.mutate();
            }}
          >
            Suppress everywhere
          </button>
        </>
      )}
    </div>
  );
}

const CONSENT_LABEL: Record<string, string> = {
  opted_in: "Opted in",
  cold: "Cold (no consent recorded)",
  unsubscribed: "Unsubscribed",
};

/**
 * Every brand, not just the ones that have mailed this person — consent can be
 * recorded before the first send, for example when someone asks to join.
 */
function ConsentCard({
  contactId,
  consent,
  onChanged,
}: {
  contactId: string;
  consent: ContactDetail["consent"];
  onChanged: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const brands = useQuery({
    queryKey: ["brands"],
    queryFn: () => api.get<Brand[]>("/config/brands"),
  });

  const set = useMutation({
    mutationFn: (args: { brandId: string; consent: string }) =>
      api.put(`/audience/contacts/${contactId}/consent/${args.brandId}`, { consent: args.consent }),
    onSuccess: () => {
      toast("Consent updated");
      setError(null);
      onChanged();
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <Card
      title="Consent by brand"
      subtitle="Unsubscribing from one brand leaves the others untouched."
    >
      {error && (
        <div className="card-body">
          <div className="error">{error}</div>
        </div>
      )}
      {brands.isLoading ? (
        <TableSkeleton />
      ) : brands.data?.length === 0 ? (
        <Empty>No brands configured.</Empty>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Brand</th>
              <th>Consent</th>
              <th>Last sent</th>
              <th>Last replied</th>
            </tr>
          </thead>
          <tbody>
            {brands.data?.map((brand) => {
              const state = consent.find((row) => row.brand_name === brand.name);
              const current = state?.consent ?? "cold";
              return (
                <tr key={brand.id}>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <BrandBadge badge={brand.badge} color={brand.color} />
                      {brand.name}
                    </div>
                  </td>
                  <td>
                    <select
                      value={current}
                      aria-label={`Consent for ${brand.name}`}
                      onChange={async (e) => {
                        const next = e.target.value;
                        if (
                          next === "unsubscribed" &&
                          !(await confirmAction({
                            title: `Unsubscribe from ${brand.name}?`,
                            message: "Anything scheduled for this brand stops too.",
                            confirmLabel: "Unsubscribe",
                            destructive: true,
                          }))
                        )
                          return;
                        set.mutate({ brandId: brand.id, consent: next });
                      }}
                    >
                      {Object.entries(CONSENT_LABEL).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                    {state?.consent_source && (
                      <span className="hint" style={{ marginLeft: 8 }}>
                        via {consentSourceLabel(state.consent_source)}
                      </span>
                    )}
                  </td>
                  <td className="mono">{fmt(state?.last_sent_at)}</td>
                  <td className="mono">{fmt(state?.last_replied_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function ListsCard({ contactId }: { contactId: string }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState("");
  const onLists = useQuery({
    queryKey: ["contact-lists", contactId],
    queryFn: () => api.get<ListRow[]>(`/audience/contacts/${contactId}/lists`),
  });
  const allLists = useQuery({
    queryKey: ["lists"],
    queryFn: () => api.get<ListRow[]>("/audience/lists"),
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["contact-lists", contactId] });
    void qc.invalidateQueries({ queryKey: ["lists"] });
  };
  const add = useMutation({
    mutationFn: (listId: string) =>
      api.post(`/audience/lists/${listId}/members`, { contactIds: [contactId] }),
    onSuccess: () => {
      toast("Added to list");
      setAdding("");
      refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (listId: string) => api.del(`/audience/lists/${listId}/members/${contactId}`),
    onSuccess: () => {
      refresh();
      toast("Removed from list");
    },
  });

  const memberOf = new Set((onLists.data ?? []).map((l) => l.id));
  const available = (allLists.data ?? []).filter((l) => !memberOf.has(l.id));

  return (
    <Card title="Lists">
      <table>
        <tbody>
          {onLists.isLoading ? (
            // A row, not <TableSkeleton />: this is already inside a table body.
            <tr>
              <td>
                <span className="skeleton" style={{ width: "40%" }} />
              </td>
            </tr>
          ) : onLists.data?.length === 0 ? (
            <tr>
              <td style={{ color: "var(--gray-500)" }}>Not on any list.</td>
            </tr>
          ) : (
            onLists.data?.map((list) => (
              <tr key={list.id}>
                <td style={{ fontWeight: 600 }}>{list.name}</td>
                <td style={{ color: "var(--gray-500)" }}>{list.brand_name}</td>
                <td style={{ textAlign: "right" }}>
                  <button
                    type="button"
                    className="btn ghost small"
                    onClick={() => remove.mutate(list.id)}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      {available.length > 0 && (
        <div className="card-body toolbar" style={{ marginBottom: 0 }}>
          <select
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
            aria-label="Add to list"
          >
            <option value="">Add to a list…</option>
            {available.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name} ({l.brand_name})
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn small"
            disabled={!adding || add.isPending}
            onClick={() => add.mutate(adding)}
          >
            Add
          </button>
        </div>
      )}
    </Card>
  );
}

/**
 * Delete is also how an erasure request is handled, so the default keeps the
 * address suppressed: otherwise the next import brings them straight back.
 */
function DeleteContact({
  contactId,
  email,
  onDeleted,
}: {
  contactId: string;
  email: string;
  onDeleted: () => void;
}) {
  const qc = useQueryClient();
  const [keepSuppressed, setKeepSuppressed] = useState(true);
  const remove = useMutation({
    mutationFn: () =>
      api.del(`/audience/contacts/${contactId}${keepSuppressed ? "" : "?suppress=false"}`),
    onSuccess: () => {
      toast("Contact deleted");
      void qc.invalidateQueries({ queryKey: ["contacts"] });
      onDeleted();
    },
  });

  return (
    <Card title="Delete contact">
      <div className="card-body">
        <p style={{ color: "var(--gray-700)", marginBottom: 12 }}>
          Removes this contact and everything recorded about them — sends, opens, replies, consent
          and list membership. Use this for erasure requests.
        </p>
        <label className="checkline" style={{ marginBottom: 12 }}>
          <input
            type="checkbox"
            checked={keepSuppressed}
            onChange={(e) => setKeepSuppressed(e.target.checked)}
          />
          Keep the address suppressed so a later import cannot add them back
        </label>
        {!keepSuppressed && (
          <div className="notice warn">
            Only untick this to correct a typo. For someone who asked to be removed, the next CSV
            that contains them would add them straight back.
          </div>
        )}
        <button
          type="button"
          className="btn danger"
          disabled={remove.isPending}
          onClick={async () => {
            if (
              await confirmAction({
                title: `Delete ${email}?`,
                message: "The contact and their history are removed for good.",
                confirmLabel: "Delete contact",
                destructive: true,
              })
            )
              remove.mutate();
          }}
        >
          Delete contact
        </button>
      </div>
    </Card>
  );
}

const fmt = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString() : "—");

/**
 * What Pulse knows about this person, as of the last sync: the lead's stage
 * and status (what lists and the status columns go by), who is working it,
 * and a way into it. An address with several leads shows each; the first is
 * the one Send uses.
 */
function PulseCard({ leads }: { leads: PulseLeadInfo[] }) {
  if (leads.length === 0) {
    return (
      <Card title="Pulse">
        <div className="card-body" style={{ color: "var(--gray-500)" }}>
          No Pulse lead has this address, so Pulse stage segments never include it.
        </div>
      </Card>
    );
  }
  const fmtDate = (v: string | null) => (v ? new Date(v).toLocaleDateString() : "—");
  return (
    <Card
      title="Pulse"
      subtitle={
        leads.length > 1
          ? `${leads.length} leads share this address. The first is the one lists use.`
          : `Synced ${new Date(leads[0]!.synced_at).toLocaleString()}`
      }
    >
      {leads.map((lead, i) => (
        <div key={lead.pulse_lead_id} className={`pulse-lead${i > 0 ? " secondary" : ""}`}>
          <div className="pulse-lead-head">
            <span className={`pill ${lead.process_stage === "lost" ? "mute" : "ok"}`}>
              {stageLabel(lead.process_stage)}
            </span>
            {lead.archived && (
              <span className="pill mute" title="Archived in Pulse, which hides it from its lists">
                Archived
              </span>
            )}
            {lead.lead_status && (
              <span
                className="hint"
                title="Set by the rep in Pulse; only the stage moves on its own"
              >
                Sub-status: {lead.lead_status}
              </span>
            )}
            {lead.disposition && <span className="hint">Disposition: {lead.disposition}</span>}
            <span className="spacer" />
            {lead.url ? (
              <a className="btn ghost small" href={lead.url} target="_blank" rel="noreferrer">
                Open lead #{lead.pulse_lead_id} ↗
              </a>
            ) : (
              <span className="mono hint">Lead #{lead.pulse_lead_id}</span>
            )}
          </div>
          <dl className="facts">
            <div>
              <dt>Assigned to</dt>
              <dd>{lead.assigned_to ?? "Unassigned"}</dd>
            </div>
            <div>
              <dt>Came in</dt>
              <dd>
                {fmtDate(lead.pulse_created_at)}
                {lead.from_form && " · filled in a form"}
              </dd>
            </div>
            <div>
              <dt>Last changed in Pulse</dt>
              <dd>{fmtDate(lead.pulse_updated_at)}</dd>
            </div>
            {lead.lead_type && lead.lead_type !== "merchant" && (
              <div>
                <dt>Type</dt>
                <dd>{lead.lead_type.replaceAll("_", " ")}</dd>
              </div>
            )}
          </dl>
        </div>
      ))}
    </Card>
  );
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <select
      aria-label={label}
      className={value ? "active" : ""}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{label}: any</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {label}: {o.label}
        </option>
      ))}
    </select>
  );
}

/**
 * The current filters, kept as a segment: a list whose membership is the
 * filter, asked again every time something is sent to it — so people join
 * and leave it as their Pulse stage moves, with nothing to re-import.
 */
function SaveSegment({
  conditions,
  brandId: initialBrand,
  brands,
  matches,
  onClose,
}: {
  conditions: Condition[];
  brandId: string;
  brands: Brand[];
  matches: number | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [brandId, setBrandId] = useState(initialBrand || brands[0]?.id || "");
  const [error, setError] = useState<string | null>(null);
  const usesConsent = conditions.some((c) => c.field === "consent");

  const save = useMutation({
    mutationFn: () =>
      api.post("/audience/lists", {
        brandId,
        name: name.trim(),
        filter: { match: "all", conditions },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["lists"] });
      toast(`Segment "${name.trim()}" saved`);
      onClose();
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <Drawer
      title="Save as segment"
      subtitle={
        matches === null
          ? undefined
          : `${matches.toLocaleString()} contacts match right now. Membership is re-checked every time it is mailed.`
      }
      onClose={onClose}
    >
      <Card>
        <form
          className="card-body"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            setError(null);
            save.mutate();
          }}
        >
          {error && <div className="error">{error}</div>}
          <div className="field">
            <label htmlFor="seg-name">Name</label>
            <input
              id="seg-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Closed — peptides, opted in"
              required
              // biome-ignore lint/a11y/noAutofocus: the panel opened for this field.
              autoFocus
            />
          </div>
          <div className="field">
            <label htmlFor="seg-brand">Brand</label>
            <select
              id="seg-brand"
              value={brandId}
              onChange={(e) => setBrandId(e.target.value)}
              disabled={usesConsent && Boolean(initialBrand)}
            >
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
            <div className="hint">
              A segment belongs to one brand: only its campaigns and sequences can use it
              {usesConsent ? ", and the consent filter is that brand's" : ""}.
            </div>
          </div>
          <button
            className="btn"
            type="submit"
            disabled={!name.trim() || !brandId || save.isPending}
          >
            {save.isPending ? "Saving…" : "Save segment"}
          </button>
        </form>
      </Card>
    </Drawer>
  );
}

function ContactTableRow({
  row,
  selected,
  picked,
  showConsent,
  onOpen,
  onToggle,
}: {
  row: ContactRow;
  selected: boolean;
  picked: boolean;
  showConsent: boolean;
  onOpen: () => void;
  onToggle: () => void;
}) {
  const consentTone =
    row.consent === "opted_in" ? "ok" : row.consent === "unsubscribed" ? "bad" : "mute";
  return (
    <tr
      className={selected ? "row-selected" : ""}
      style={{ cursor: "pointer" }}
      tabIndex={0}
      aria-label={`Open ${row.email}`}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen();
      }}
    >
      <td className="tick">
        <input
          type="checkbox"
          aria-label={`Select ${row.email}`}
          checked={picked}
          // Ticking a row must not also open it.
          onClick={(e) => e.stopPropagation()}
          onChange={onToggle}
        />
      </td>
      <td>
        <div style={{ fontWeight: 600 }}>
          {[row.first_name, row.last_name].filter(Boolean).join(" ") || "—"}
        </div>
        <div className="mono" style={{ color: "var(--gray-500)", fontSize: 11 }}>
          {row.email}
        </div>
      </td>
      <td>{row.company ?? "—"}</td>
      <td>
        {row.pulse_stage ? (
          <div className="stage-cell">
            <span className={`pill ${row.pulse_stage === "lost" ? "mute" : "ok"}`}>
              {stageLabel(row.pulse_stage)}
            </span>
            {row.pulse_status && (
              <span className="hint" title="Pulse sub-status: set by the rep, not the system">
                Sub-status: {row.pulse_status}
              </span>
            )}
            {row.pulse_disposition && (
              <span className="hint" title="Pulse disposition">
                {row.pulse_disposition}
              </span>
            )}
          </div>
        ) : (
          <span style={{ color: "var(--gray-400)" }}>—</span>
        )}
      </td>
      <td style={{ fontSize: 12 }}>{row.pulse_rep ?? "—"}</td>
      {showConsent && (
        <td>
          <span className={`pill ${consentTone}`}>
            {CONSENT_LABEL[row.consent ?? "cold"]?.split(" (")[0] ?? row.consent}
          </span>
        </td>
      )}
      <td className="mono">{new Date(row.created_at).toLocaleDateString()}</td>
      <td style={{ textAlign: "right" }}>
        {row.suppressed && <span className="pill bad">Suppressed</span>}
      </td>
    </tr>
  );
}

function ContactFilters({
  filters,
  filtering,
  facets,
  brands,
  setFilter,
}: {
  filters: Record<FilterKey, string>;
  filtering: boolean;
  facets: PulseFacets | undefined;
  brands: Brand[];
  setFilter: (changes: Partial<Record<FilterKey, string>>) => void;
}) {
  return (
    <fieldset className="filters">
      <legend className="sr-only">Filters</legend>
      <FilterSelect
        label="Pulse stage"
        value={filters.stage}
        onChange={(v) => setFilter({ stage: v })}
        options={[
          ...(facets?.stages ?? []).map((f) => ({
            value: f.value,
            label: `${stageLabel(f.value)} (${f.n.toLocaleString()})`,
          })),
          { value: "none", label: "No Pulse lead" },
        ]}
      />
      <FilterSelect
        label="Sub-status"
        value={filters.status}
        onChange={(v) => setFilter({ status: v })}
        options={(facets?.statuses ?? []).map((f) => ({
          value: f.value,
          label: `${f.value} (${f.n.toLocaleString()})`,
        }))}
      />
      <FilterSelect
        label="Disposition"
        value={filters.disposition}
        onChange={(v) => setFilter({ disposition: v })}
        options={(facets?.dispositions ?? []).map((f) => ({
          value: f.value,
          label: `${f.label} (${f.n.toLocaleString()})`,
        }))}
      />
      <FilterSelect
        label="Rep"
        value={filters.rep}
        onChange={(v) => setFilter({ rep: v })}
        options={(facets?.reps ?? []).map((f) => ({
          value: f.value,
          label: `${f.value} (${f.n.toLocaleString()})`,
        }))}
      />
      <FilterSelect
        label="Came from"
        value={filters.form}
        onChange={(v) => setFilter({ form: v })}
        options={[
          { value: "yes", label: "Filled in a form" },
          { value: "no", label: "Added by a rep or import" },
        ]}
      />
      <FilterSelect
        label="Archived in Pulse"
        value={filters.archived}
        onChange={(v) => setFilter({ archived: v })}
        options={[
          { value: "no", label: "Live leads" },
          { value: "yes", label: "Archived leads" },
        ]}
      />
      <FilterSelect
        label="Brand"
        value={filters.brand}
        onChange={(v) => setFilter({ brand: v })}
        options={brands.map((b) => ({ value: b.id, label: b.name }))}
      />
      {filters.brand && (
        <FilterSelect
          label="Consent"
          value={filters.consent}
          onChange={(v) => setFilter({ consent: v })}
          options={[
            { value: "opted_in", label: "Opted in" },
            { value: "cold", label: "Cold" },
            { value: "unsubscribed", label: "Unsubscribed" },
          ]}
        />
      )}
      <FilterSelect
        label="Suppressed"
        value={filters.suppressed}
        onChange={(v) => setFilter({ suppressed: v })}
        options={[
          { value: "no", label: "Not suppressed" },
          { value: "yes", label: "Suppressed" },
        ]}
      />
      {(filtering || filters.brand) && (
        <button
          type="button"
          className="btn ghost small"
          onClick={() =>
            setFilter(Object.fromEntries(FILTER_KEYS.map((k) => [k, ""])) as Record<FilterKey, "">)
          }
        >
          Clear filters
        </button>
      )}
    </fieldset>
  );
}

/**
 * The page's state, kept in the URL: the open contact, the search, the page
 * and the filters. A view survives a refresh, can be linked to, and Back
 * steps back through it.
 */
function useContactsUrl() {
  const [params, setParams] = useSearchParams();
  const offset = Math.max(Number(params.get("page") ?? 1) - 1, 0) * PAGE;
  const filters = Object.fromEntries(FILTER_KEYS.map((k) => [k, params.get(k) ?? ""])) as Record<
    FilterKey,
    string
  >;
  const setFilter = (changes: Partial<Record<FilterKey | "q" | "page", string>>) =>
    setParams(
      (current) => {
        const p = new URLSearchParams(current);
        for (const [k, v] of Object.entries(changes)) {
          if (v) p.set(k, v);
          else p.delete(k);
        }
        // Any change to what is shown starts again from the first page.
        if (!("page" in changes)) p.delete("page");
        // Consent means nothing without a brand to have it for.
        if ("brand" in changes && !changes.brand) p.delete("consent");
        return p;
      },
      { replace: true },
    );
  const setOffset = (next: number) => setFilter({ page: next > 0 ? String(next / PAGE + 1) : "" });

  const selected = params.get("contact");
  const adding = params.has("new");
  const open = useCallback(
    (next: { contact?: string } | { new: true } | null, options: { replace?: boolean } = {}) =>
      setParams(
        (current) => {
          const p = new URLSearchParams(current);
          p.delete("contact");
          p.delete("new");
          if (next && "contact" in next && next.contact) p.set("contact", next.contact);
          if (next && "new" in next) p.set("new", "1");
          return p;
        },
        { replace: options.replace ?? false },
      ),
    [setParams],
  );
  const close = useCallback(() => open(null), [open]);
  return { params, offset, filters, setFilter, setOffset, selected, adding, open, close };
}

function Pager({
  offset,
  total,
  onChange,
}: {
  offset: number;
  total: number;
  onChange: (offset: number) => void;
}) {
  if (total <= PAGE) return null;
  return (
    <div className="toolbar">
      <button
        type="button"
        className="btn ghost small"
        disabled={offset === 0}
        onClick={() => onChange(Math.max(offset - PAGE, 0))}
      >
        ← Previous
      </button>
      <span style={{ color: "var(--gray-500)" }}>
        {(offset + 1).toLocaleString()}–{Math.min(offset + PAGE, total).toLocaleString()} of{" "}
        {total.toLocaleString()}
      </span>
      <button
        type="button"
        className="btn ghost small"
        disabled={offset + PAGE >= total}
        onClick={() => onChange(offset + PAGE)}
      >
        Next →
      </button>
    </div>
  );
}

function ContactsTable({
  loading,
  rows,
  searching,
  showConsent,
  selected,
  picked,
  setPicked,
  onOpen,
}: {
  loading: boolean;
  rows: ContactRow[] | undefined;
  searching: boolean;
  showConsent: boolean;
  selected: string | null;
  picked: Set<string>;
  setPicked: (update: (current: Set<string>) => Set<string>) => void;
  onOpen: (id: string) => void;
}) {
  return (
    <Card>
      {loading ? (
        <TableSkeleton cols={6} />
      ) : rows?.length === 0 ? (
        <Empty>
          {searching
            ? "No contacts match."
            : "No contacts yet. Add one, import a CSV, or connect Pulse."}
        </Empty>
      ) : (
        <table>
          <thead>
            <tr>
              <th className="tick">
                <input
                  type="checkbox"
                  aria-label="Select every contact on this page"
                  checked={(rows?.length ?? 0) > 0 && (rows ?? []).every((r) => picked.has(r.id))}
                  onChange={(e) =>
                    setPicked((current) => {
                      const next = new Set(current);
                      for (const r of rows ?? []) {
                        if (e.target.checked) next.add(r.id);
                        else next.delete(r.id);
                      }
                      return next;
                    })
                  }
                />
              </th>
              <th>Contact</th>
              <th>Company</th>
              <th>Pulse</th>
              <th>Rep</th>
              {showConsent && <th>Consent</th>}
              <th>Added</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows?.map((row) => (
              <ContactTableRow
                key={row.id}
                row={row}
                selected={selected === row.id}
                picked={picked.has(row.id)}
                showConsent={showConsent}
                onOpen={() => onOpen(row.id)}
                onToggle={() =>
                  setPicked((current) => {
                    const next = new Set(current);
                    if (!next.delete(row.id)) next.add(row.id);
                    return next;
                  })
                }
              />
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

/** The list's own query, for the CSV link: what you see is what downloads. */
function exportQuery(q: string, filters: Record<FilterKey, string>, conditions: Condition[]) {
  const search = new URLSearchParams({ q });
  if (filters.brand) search.set("brandId", filters.brand);
  if (conditions.length) search.set("filter", JSON.stringify({ match: "all", conditions }));
  return search.toString();
}

/** The editable copy of a contact's details: blanks rather than nulls. */
function detailsForm(contact: ContactDetail["contact"]) {
  return {
    firstName: contact.firstName ?? "",
    lastName: contact.lastName ?? "",
    company: contact.company ?? "",
    title: contact.title ?? "",
    phone: contact.phone ?? "",
    timezone: contact.timezone ?? "",
  };
}

/** A contact's zone: set by hand, or blank to go by their phone's area code. */
function TimezoneField({
  id,
  value,
  zone,
  onChange,
}: {
  id: string;
  value: string;
  zone: ContactDetail["timezone"];
  onChange: (zone: string) => void;
}) {
  const fromPhone = zone.source === "phone" && zone.zone ? zone.zone : null;
  return (
    <div className="field">
      <label htmlFor={id}>Time zone</label>
      <TimezoneSelect
        id={id}
        value={value}
        onChange={onChange}
        allowBlank
        blankLabel={fromPhone ? `From phone: ${fromPhone}` : "Not known"}
      />
      <div className="hint">
        {value
          ? "Set by hand: local-time sends use this."
          : fromPhone
            ? "Worked out from the phone's area code. Pick one to override it."
            : "Local-time sends use the campaign's fallback zone."}
      </div>
    </div>
  );
}
